import { useEffect, useRef, useState } from "react"
import Api from "../state/Api"
import MonApi from "../state/mongo-api"

const server = process.env.REACT_APP_FILE_SERVER

const CIPL_LABELS = ['Commercial Invoice', 'Packing List']
const POLL_MS = 10000
const RETRY_POLL_MS = 30000

// Customer Submit: queue the verification on the server without waiting, so it is ready by the time Agraga opens the task
// Never throws: a failure here must not break the customer's Submit, the Agraga side queues it again when the task is opened
export function queueCiplVerification(task) {
    try {
        const files = ciplFiles(task)
        if (files.length) Api.queueCiplVerify({ bookingNum: task.bookingNum, taskNum: task._id?.num, files })
    } catch (err) {
        console.log("CIPL verification queue failed:", err)
    }
}

function ciplFiles(task) {
    return (task.files || [])
        .filter(e => CIPL_LABELS.includes(e.label) && e.filename?.length > 0)
        .map(e => ({ label: e.label, filename: e.filename }))
}

function sameFiles(a = [], b = []) {
    const key = (l) => l.map(f => f.label + '|' + f.filename).sort().join(',')
    return key(a) === key(b)
}

function DocumentTable({ title, docs, tagClass }) {
    if (!docs.length) return <></>
    return (
        <>
            <h6 className="title is-6" style={{ marginBottom: '0.5rem' }}>{title} ({docs.length})</h6>
            <table className="table is-bordered is-narrow is-fullwidth">
                <thead>
                    <tr><th>Document</th><th>File</th><th>Status</th><th>Remarks</th></tr>
                </thead>
                <tbody>
                    {docs.map(d => (
                        <tr key={d.label + d.filename}>
                            <td>{d.label}</td>
                            <td>{d.filename}</td>
                            <td>
                                <span className={'tag ' + tagClass}>{d.status === 'error' ? 'Not Verified' : d.status === 'matched' ? 'Matched' : 'Unmatched'}</span>
                                {d.verifiedEarlier && <>&nbsp;<span className="tag is-light">Verified earlier</span></>}
                            </td>
                            <td>
                                {d.reasons?.length > 0
                                    ? <ul>{d.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                                    : d.pairedWith?.length > 0 ? 'Matched with ' + d.pairedWith.join(', ') : ''}
                                {d.mismatchedFields?.length > 0 && (
                                    <ul className="has-text-danger">{d.mismatchedFields.map((r, i) => <li key={i}>Mismatch - {r}</li>)}</ul>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    )
}

function money(m) {
    if (!m || m.amount == null) return ''
    const a = typeof m.amount === 'number' ? m.amount.toLocaleString(undefined, { maximumFractionDigits: 4 }) : m.amount
    return `${a} ${m.currency || ''}`.trim()
}

// The line's value in its invoice currency, and in USD as sent to the HTS classifier
function CargoValue({ line }) {
    const original = money(line.cargoValue)
    const usd = line.cargoValueUsd != null ? money({ amount: line.cargoValueUsd, currency: 'USD' }) : null
    if (!original) return '-'
    if (String(line.cargoValue.currency || '').toUpperCase() === 'USD') return original
    return (
        <>
            <div>{original}</div>
            <div className="has-text-grey is-size-7">{usd || 'USD: rate not known'}</div>
        </>
    )
}

const STATUS_CLASS = { ok: 'has-text-success', mismatch: 'has-text-danger', not_available: 'has-text-grey' }

function LineItems({ invoice }) {
    if (!invoice.lineItems?.length) return <></>
    return (
        <>
            <h6 className="title is-6" style={{ marginBottom: '0.5rem' }}>
                Line Items - Invoice {invoice.invoiceNumber || ''} <span className="has-text-grey is-size-7">({invoice.fileName})</span>
            </h6>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                <table className="table is-bordered is-narrow is-fullwidth is-striped">
                    <thead>
                        <tr><th>#</th><th>Description</th><th>Product Code</th><th>HS</th><th className="has-text-right">Qty</th><th className="has-text-right">Unit Price</th><th className="has-text-right">Line Total</th><th className="has-text-right">Cargo Value</th><th>Flags</th></tr>
                    </thead>
                    <tbody>
                        {invoice.lineItems.map((l, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{l.description}</td>
                                <td>{l.productCode}</td>
                                <td>{l.hsCode}</td>
                                <td className="has-text-right">{[l.quantity, l.unitOfMeasure].filter(v => v != null && v !== '').join(' ')}</td>
                                <td className="has-text-right">{money(l.unitPrice)}</td>
                                <td className="has-text-right">{money(l.lineTotal)}</td>
                                <td className="has-text-right"><CargoValue line={l} /></td>
                                <td>{(l.flags || []).join(', ')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    )
}

function Reconciliation({ rec }) {
    if (!rec) return <></>
    const loose = [
        ...(rec.unpairedInvoices || []).map(i => `Invoice ${i.invoiceNumber ?? '(no number read)'}`),
        ...(rec.unpairedPackingLists || []).map(p => `Packing List ${p.packingListNumber ?? ''} (references invoice ${p.relatedInvoiceNumber ?? 'none'})`),
    ]
    return (
        <>
            {(rec.pairs || []).map((p, i) => (
                <div key={i} style={{ marginBottom: '1rem' }}>
                    <h6 className="title is-6" style={{ marginBottom: '0.5rem' }}>
                        CI ⇄ PL Reconciliation {rec.pairs.length > 1 ? `- Invoice ${p.invoiceNumber ?? ''}` : ''}&nbsp;
                        <span className={'tag ' + (p.matched ? 'is-success' : 'is-danger')}>{p.matched ? 'Matched' : 'Not Matched'}</span>
                    </h6>
                    <table className="table is-bordered is-narrow is-fullwidth">
                        <thead>
                            <tr><th>Check</th><th>Invoice</th><th>Packing List</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                            {(p.checks || []).map(c => (
                                <tr key={c.name}>
                                    <td style={{ textTransform: 'capitalize' }}>{c.name.replace(/_/g, ' ')}</td>
                                    <td>{c.ci ?? '—'}</td>
                                    <td>{c.pl ?? '—'}</td>
                                    <td className={STATUS_CLASS[c.status]}>{(c.status || '').replace(/_/g, ' ')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {p.flags?.length > 0 && <div className="tags">{p.flags.map(f => <span key={f} className="tag is-warning is-light">{f}</span>)}</div>}
                </div>
            ))}
            {loose.length > 0 && <p className="has-text-grey" style={{ marginBottom: '1rem' }}>Not checked, no invoice-number match: {loose.join('; ')}</p>}
        </>
    )
}

// Sets the approval table from the verification each time a result loads: matched → Approve, unmatched → Reject with its reasons.
// The user can still change a row afterwards; it is only reset when the verification is loaded or re-run.
async function applyVerdicts(task, docs) {
    for (const d of docs) {
        if (d.status !== 'matched' && d.status !== 'unmatched') continue
        const f = (task.files || []).find(e => e.label === d.label && e.filename === d.filename)
        if (!f || f.nonEditable) continue
        const approve = d.status === 'matched'
        const value = approve ? 'Approve' : 'Reject'
        if (f.approved === value && (approve || f.rejectReason)) continue
        f.approved = value
        f.approvedat = new Date().toLocaleDateString()
        f.rejectReason = approve ? '' : (d.reasons?.length ? d.reasons.join('; ') : 'Commercial Invoice and Packing List do not match')
        await MonApi.get(server + '/api/v1/filestore/' + (approve ? 'approvefile' : 'clearapproval') + '/' + task.bookingNum + '/' + f.filename)
    }
}

// Agraga-side check of the uploaded CI & PL against the CI-PL parser; the full parser response is stored by the server
// onBusyChange(true|false) lets the page lock the approval table and Save while a verification is running.
// readOnly (approved action): shows the saved result only, never queues a check, polls, or changes the approval values.
export default function CiplVerification({ task, onBusyChange = (f) => f, readOnly = false }) {
    const [verification, setVerification] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [retry, setRetry] = useState(null)
    const [progress, setProgress] = useState(null)
    const pollTimer = useRef(null)
    // Bumped when the user leaves this action or opens another task. A reply or timer from an earlier session is
    // ignored, so it neither restarts polling nor touches the task now on screen.
    const session = useRef(0)

    // The server runs verifications as background jobs; this queues one (or joins the one already running) and waits for it
    function verify() {
        const files = ciplFiles(task)
        if (!files.length) return setLoading(false)
        const s = session.current
        setLoading(true)
        setError('')
        Api.verifyCipl({ bookingNum: task.bookingNum, taskNum: task._id?.num, files }, (res) => {
            if (s !== session.current) return
            if (res.success) schedulePoll()
            else {
                setLoading(false)
                setError(res.message || 'Verification failed')
            }
        }, () => s === session.current && setLoading(false))
    }

    function schedulePoll(ms = POLL_MS) {
        clearTimeout(pollTimer.current)
        const s = session.current
        pollTimer.current = setTimeout(() => s === session.current && load(false), ms)
    }

    // allowQueue: on first load, queue a verification when none exists for the current files
    function load(allowQueue) {
        const s = session.current
        Api.getCiplVerification({ bookingNum: task.bookingNum, taskNum: task._id?.num }, (res) => {
            if (s !== session.current) return
            if (!res.success) {
                setLoading(false)
                return setError(res.message || 'Verification failed')
            }
            if (readOnly) {
                setVerification(res.data || null)
                return setLoading(false)
            }
            const files = ciplFiles(task)
            const job = res.job && sameFiles(res.job.files, files) ? res.job : null
            const open = job && (job.status === 'pending' || job.status === 'running')
            const current = res.data && sameFiles(res.data.files, files) ? res.data : null
            // These files are still being checked: the approval table waits, but the progress and the last result are shown
            if (open && !current) {
                setLoading(true)
                setProgress(job)
                if (res.data) setVerification(prev => (prev?._id === res.data._id ? prev : res.data))
                return schedulePoll()
            }
            setProgress(null)
            if (current) {
                // Same record keeps the same object, so a poll does not re-apply verdicts over the user's own choices
                setVerification(prev => (prev?._id === current._id ? prev : current))
                setError('')
                setLoading(false)
                // An automatic recheck of unread files is queued or running: show what we have and keep watching for it
                setRetry(open ? job : null)
                if (open) schedulePoll(RETRY_POLL_MS)
                return
            }
            if (job?.status === 'failed') {
                setLoading(false)
                return setError(job.error || 'Verification failed')
            }
            if (allowQueue) verify()
            else setLoading(false)
        }, () => s === session.current && setLoading(false))
    }

    useEffect(() => {
        if (!task?.bookingNum) return
        session.current++
        setLoading(true)
        load(true)
        return () => {
            session.current++
            clearTimeout(pollTimer.current)
        }
    }, [task?.bookingNum, task?._id?.num])

    useEffect(() => {
        if (!readOnly && verification?.documents?.length) applyVerdicts(task, verification.documents)
    }, [verification])

    useEffect(() => {
        onBusyChange(loading)
        return () => onBusyChange(false)
    }, [loading])

    const docs = verification?.documents || []
    const matched = docs.filter(d => d.status === 'matched')
    const unmatched = docs.filter(d => d.status === 'unmatched')
    const failed = docs.filter(d => d.status === 'error')

    return (
        <div className="box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <strong>AI Document Verification</strong>
                {!readOnly && (
                    <button className={'button is-small islink' + (loading ? ' is-loading' : '')} disabled={loading} onClick={verify}>
                        Re-verify
                    </button>
                )}
            </div>
            {readOnly && !loading && !error && !verification && <p className="has-text-grey">No AI document verification was recorded for this action.</p>}
            {loading && !readOnly && (
                <div className="notification is-info is-light" style={{ padding: '0.75rem 1rem' }}>
                    <p>
                        Verifying Commercial Invoice & Packing List
                        {progress?.status === 'running' && progress.startedOn
                            ? ` - running for ${Math.max(1, Math.round((Date.now() - new Date(progress.startedOn)) / 60000))} min`
                            : progress?.status === 'pending' ? ' - waiting to start' : ''}
                        . A PDF read by AI can take a few minutes; the result shows here by itself.
                    </p>
                    {progress?.checkingFiles?.length > 0 && (
                        <>
                            <p style={{ marginTop: '0.5rem' }}>Reading now ({progress.checkingFiles.length}):</p>
                            <ul style={{ listStyle: 'disc', marginLeft: '1.5rem' }}>
                                {progress.checkingFiles.map(f => <li key={f.filename}>{f.label} - {f.filename}</li>)}
                            </ul>
                        </>
                    )}
                    {verification && <p className="has-text-grey" style={{ marginTop: '0.5rem' }}>Below is the last result, for the files checked before.</p>}
                </div>
            )}
            {!loading && error && <p className="has-text-danger">{error}</p>}
            {!loading && retry && (
                <p className="has-text-info" style={{ marginBottom: '0.75rem' }}>
                    {retry.status === 'running'
                        ? 'Re-checking the files that could not be read...'
                        : `Files that could not be read will be checked again automatically${retry.notBefore ? ' at ' + new Date(retry.notBefore).toLocaleTimeString() : ''} (retry ${retry.retryCount} of ${retry.maxRetries}).`}
                </p>
            )}
            {verification && (
                <>
                    <p style={{ marginBottom: '0.75rem' }}>
                        <span className="tag is-success">Matched: {matched.length}</span>&nbsp;
                        <span className="tag is-danger">Unmatched: {unmatched.length + failed.length}</span>
                    </p>
                    <DocumentTable title="Matched Documents" docs={matched} tagClass="is-success" />
                    <DocumentTable title="Unmatched Documents" docs={unmatched} tagClass="is-danger" />
                    <DocumentTable title="Could Not Verify" docs={failed} tagClass="is-warning" />
                    {(verification.runs || []).map((r, i) => (
                        <div key={i}>
                            {r.invoices?.map((inv, j) => <LineItems key={j} invoice={inv} />)}
                            <Reconciliation rec={r.reconciliation} />
                        </div>
                    ))}
                </>
            )}
        </div>
    )
}
